import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";

const ACCOUNTING_URL_PREFIXES = ["/api/accounting/", "/api/warehouse/"];

const PASSTHROUGH_URL_PREFIXES = [
  "/api/warehouse/public/",
  "/api/warehouse/requests/",
  "/api/accounting/business-trips/create/",
];

const FORWARDED_URL_PREFIX = "/api/accounting/";

const ONE_C_SERVED_PATH =
  /^\/api\/accounting\/(capabilities|journal-entries(\/\d+)?|trial-balance|pl-report|cash-flow(\/time-series)?)\/$/;

const TIMESHEET_OWNED_PREFIXES = [
  "/api/accounting/timesheets/",
  "/api/accounting/combined-timesheets/",
  "/api/accounting/timesheet-entries/",
  "/api/accounting/national-holidays/",
];

const E_NGO_OWNED_PREFIXES = [...TIMESHEET_OWNED_PREFIXES, "/api/accounting/payrolls/"];

const READ_METHOD = "GET";

export type OneCDecision =
  | { kind: "passthrough" }
  | { kind: "forward"; url: string }
  | { kind: "block"; error: FetchBaseQueryError };

export function isENgoOwnedAccountingUrl(path: string): boolean {
  return E_NGO_OWNED_PREFIXES.some(function matchesOwned(prefix) {
    return path.startsWith(prefix);
  });
}

export function isAccountingUrl(path: string): boolean {
  const passesThrough = PASSTHROUGH_URL_PREFIXES.some(function matchesPassthrough(prefix) {
    return path.startsWith(prefix);
  });
  if (passesThrough) return false;

  return ACCOUNTING_URL_PREFIXES.some(function matchesAccounting(prefix) {
    return path.startsWith(prefix);
  });
}

function readPath(url: string): string {
  const queryStart = url.indexOf("?");
  return queryStart === -1 ? url : url.slice(0, queryStart);
}

function readQuery(url: string): string {
  const queryStart = url.indexOf("?");
  return queryStart === -1 ? "" : url.slice(queryStart);
}

function readRequest(args: string | FetchArgs): { url: string; method: string } {
  if (typeof args === "string") return { url: args, method: READ_METHOD };
  return { url: args.url, method: (args.method ?? READ_METHOD).toUpperCase() };
}

function buildBlockedError(path: string, reason: string): FetchBaseQueryError {
  return { status: 404, data: { detail: reason, path } };
}

export function buildOneCUrl(url: string): string {
  return url.slice(FORWARDED_URL_PREFIX.length - 1);
}

export function resolveOneCRequest(args: string | FetchArgs, servesEveryRead = false): OneCDecision {
  const { url, method } = readRequest(args);
  const path = readPath(url);

  if (!isAccountingUrl(path)) return { kind: "passthrough" };

  const isTimesheetUrl = TIMESHEET_OWNED_PREFIXES.some(function matchesTimesheet(prefix) {
    return path.startsWith(prefix);
  });
  if (isTimesheetUrl) return { kind: "passthrough" };

  if (method !== READ_METHOD) {
    return { kind: "block", error: buildBlockedError(path, "accounting_read_only") };
  }

  if (isENgoOwnedAccountingUrl(path)) {
    return { kind: "passthrough" };
  }

  if (!path.startsWith(FORWARDED_URL_PREFIX)) {
    return { kind: "passthrough" };
  }

  if (!servesEveryRead && !ONE_C_SERVED_PATH.test(path)) {
    return { kind: "block", error: buildBlockedError(path, "accounting_unavailable") };
  }

  return { kind: "forward", url: buildOneCUrl(path) + readQuery(url) };
}
